import GenericTable from "components/table";
import { SubscriptionPlan } from "types";
import React, { useEffect, useMemo, useState } from "react";
import { fetchSubscriptionFrequencies } from "api/axios";
import { DropdownOption } from "components/pure-dropdown";
import { DATE_TIME_FORMAT } from "constants/date";
import dayjs from "dayjs";
import CreateSubscriptionPlanDialog from "./CreateSubscriptionPlanDialog";
import ViewSubscriptionPlanDialog from "./ViewSubscriptionPlanDialog";
import CloneSubscriptionPlanDialog from "./CloneSubscriptionPlanDialog";
import { Copy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getSubscriptionFrequencyTranslationKey } from "utils";
// Pagination import removed

type SubscriptionPlansTableProps = {
  listManagement: {
    data: SubscriptionPlan[];
    total: number;
    isLoading: boolean;
    error: unknown;
    refetch: () => void;
  };
  initialSubscriptionPlanId?: string | null;
};

const SubscriptionPlansDataTable: React.FC<SubscriptionPlansTableProps> = ({
  listManagement,
  initialSubscriptionPlanId,
}) => {
  const { t } = useTranslation();
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] =
    useState<SubscriptionPlan | null>(null);

  // Create dialog state
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Clone dialog state
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false);
  const [cloneSourcePlan, setCloneSourcePlan] =
    useState<SubscriptionPlan | null>(null);

  // Frequency list fetched once; options derived via useMemo
  const [rawFrequencies, setRawFrequencies] = useState<any[]>([]);
  // Loading/error retained internally if needed for future UX; currently unused

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await fetchSubscriptionFrequencies();
        const list = Array.isArray(data?.subscription_frequencies)
          ? data.subscription_frequencies
          : Array.isArray(data)
          ? data
          : [];
        if (mounted) setRawFrequencies(list);
      } catch (e: any) {
        // swallow error; dialogs will show empty options
      } finally {
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const frequencyOptions: DropdownOption[] = useMemo(() => {
    return rawFrequencies.map((item: any) => {
      if (
        item &&
        typeof item === "object" &&
        "value" in item &&
        "label" in item
      ) {
        const translatedLabel = t(
          getSubscriptionFrequencyTranslationKey(String(item.value))
        );
        return { value: String(item.value), label: translatedLabel };
      }
      const value = String(item);
      const translatedLabel = t(getSubscriptionFrequencyTranslationKey(value));
      return { value, label: translatedLabel };
    });
  }, [rawFrequencies, t]);

  // Helper function to get subscription plan status
  const getSubscriptionPlanStatus = (subscriptionPlan: SubscriptionPlan) => {
    if (subscriptionPlan.is_active) {
      return {
        text: t("subscriptionPlans.active"),
        class:
          "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
      };
    } else {
      return {
        text: t("subscriptionPlans.inactive"),
        class: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
      };
    }
  };

  // Memoize data with computed status
  const dataWithComputedStatus = useMemo(() => {
    return listManagement.data.map((subscriptionPlan) => ({
      ...subscriptionPlan,
      computedStatus: getSubscriptionPlanStatus(subscriptionPlan),
    }));
  }, [listManagement.data, t]);

  const handleCreate = () => {
    setIsCreateDialogOpen(true);
  };

  const handleViewSubscriptionPlan = (subscriptionPlan: SubscriptionPlan) => {
    setSelectedSubscriptionPlan(subscriptionPlan);
    setIsViewDialogOpen(true);
  };

  // Auto-open dialog if initialSubscriptionPlanId is provided (from notification)
  useEffect(() => {
    if (
      initialSubscriptionPlanId &&
      !isViewDialogOpen &&
      listManagement.data.length > 0
    ) {
      const subscriptionPlan = listManagement.data.find(
        (plan: SubscriptionPlan) => plan.id === initialSubscriptionPlanId
      );
      if (subscriptionPlan) {
        handleViewSubscriptionPlan(subscriptionPlan);
      }
    }
  }, [initialSubscriptionPlanId, listManagement.data, isViewDialogOpen]);

  const columns = [
    {
      accessorKey: "name",
      header: () => <p>{t("common.name")}</p>,
      cell: (info: any) => (
        <p className="max-w-xs truncate text-sm text-navy-700 dark:text-white">
          {info.getValue() || t("library.noNameProvided")}
        </p>
      ),
    },
    {
      accessorKey: "is_active",
      header: () => <p>{t("library.status")}</p>,
      cell: (info: any) => {
        const subscriptionPlan = info.row.original as any;
        const status = subscriptionPlan.computedStatus;

        return (
          <div className="flex items-center">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${status.class}`}
            >
              {status.text}
            </span>
          </div>
        );
      },
      meta: {
        filterable: true,
        filterOptions: [
          { label: t("subscriptionPlans.active"), value: "active" },
          { label: t("subscriptionPlans.inactive"), value: "inactive" },
        ],
      },
      filterFn: (row: any, columnId: string, filterValue: string[]) => {
        const subscriptionPlan = row.original as SubscriptionPlan;
        // Use is_active boolean directly instead of comparing translated text
        const currentStatus = subscriptionPlan.is_active
          ? "active"
          : "inactive";
        return filterValue.includes(currentStatus);
      },
    },
    {
      accessorKey: "created_at",
      header: () => <p>{t("library.createdDate")}</p>,
      cell: (info: any) => (
        <p className="text-sm text-navy-700 dark:text-white">
          {info.getValue()
            ? dayjs(info.getValue()).format(DATE_TIME_FORMAT)
            : "N/A"}
        </p>
      ),
    },
    {
      accessorKey: "updated_at",
      header: () => <p>{t("library.updatedDate")}</p>,
      cell: (info: any) => (
        <p className="text-sm text-navy-700 dark:text-white">
          {info.getValue()
            ? dayjs(info.getValue()).format(DATE_TIME_FORMAT)
            : "N/A"}
        </p>
      ),
    },
    {
      id: "actions",
      header: (): null => null,
      cell: (info: any) => {
        const rowPlan = info.row.original as SubscriptionPlan;
        return (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              onClick={(e) => {
                e.stopPropagation();
                setCloneSourcePlan(rowPlan);
                setIsCloneDialogOpen(true);
              }}
            >
              <Copy size={14} />
              {t("library.clone")}
            </button>
          </div>
        );
      },
    },
  ];

  const dataWithActions = dataWithComputedStatus.map((row) => ({
    ...row,
  }));

  return (
    <div className="flex h-full flex-col">
      <GenericTable
        data={dataWithActions}
        columns={columns as any}
        searchPlaceholder={t("subscriptionPlans.searchPlans")}
        debug={true}
        onRowClick={(item) => {
          handleViewSubscriptionPlan(item);
        }}
        onAdd={handleCreate}
        addLabel={t("subscriptionPlans.addPlan")}
        maxHeight="flex-1"
      />

      <div className="mt-auto">{/* Pagination removed */}</div>

      <CreateSubscriptionPlanDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={listManagement.refetch}
        frequencyOptions={frequencyOptions}
      />

      <CloneSubscriptionPlanDialog
        isOpen={isCloneDialogOpen}
        onClose={() => setIsCloneDialogOpen(false)}
        onSuccess={listManagement.refetch}
        subscriptionPlan={cloneSourcePlan}
        frequencyOptions={frequencyOptions}
      />

      <ViewSubscriptionPlanDialog
        isOpen={isViewDialogOpen}
        onClose={() => setIsViewDialogOpen(false)}
        onSuccess={listManagement.refetch}
        subscriptionPlan={selectedSubscriptionPlan}
        frequencyOptions={frequencyOptions}
      />
    </div>
  );
};

export default SubscriptionPlansDataTable;
